import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';

const TermsOfService = () => {
  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <Seo
        title="Terms of Service | MoveraCare Mobility"
        description="Terms of Service for MoveraCare Mobility non-emergency medical transportation services across Greater Houston."
        path="/terms"
        noindex
      />
      {/* Header */}
      <section className="bg-[#004C97] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#F9A825] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <FileText className="w-12 h-12 text-[#F9A825]" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">Terms of Service</h1>
          </div>
          <p className="text-gray-200">
            <strong>Effective Date:</strong> April 25, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
          <div>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") govern your use of MoveraCare Services LLC's ("MoveraCare") non-emergency medical transportation services. By booking a ride with us, you agree to these terms.
            </p>
          </div>

          {/* Emergency Notice */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-2">EMERGENCY MEDICAL SERVICES</h3>
                <p className="text-red-800">
                  MoveraCare provides Non-Emergency Medical Transportation (NEMT) only. We are <strong>NOT</strong> an emergency medical service. <strong>In the event of a medical emergency, call 911 immediately.</strong>
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">1. Service Description</h2>
            <p className="text-gray-700 mb-4">
              MoveraCare Mobility provides professional non-emergency medical transportation services including:
            </p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Wheelchair-accessible transportation</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Ambulatory sedan transport</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Dialysis and recurring medical appointments</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Hospital discharge transportation</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4 flex items-center">
              <Clock className="w-6 h-6 mr-2 text-[#F9A825]" />
              2. Scheduling & Cancellations
            </h2>
            
            <div className="space-y-4">
              <div className="bg-[#F9A825]/10 border-l-4 border-[#F9A825] p-4 rounded">
                <h3 className="font-semibold text-[#004C97] mb-2">Wait Time Policy</h3>
                <p className="text-gray-700">
                  Drivers will wait for <strong>15 minutes</strong> past the scheduled pick-up time. After this period, the trip may be marked as a "No-Show" and standard fees will apply.
                </p>
              </div>

              <div className="bg-[#F9A825]/10 border-l-4 border-[#F9A825] p-4 rounded">
                <h3 className="font-semibold text-[#004C97] mb-2">Cancellation Policy</h3>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">We require at least <strong>2 hours' notice</strong> for cancellations.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">Late cancellations (less than 2 hours) may incur a fee of <strong>75% of the base rate</strong>.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">No-show fees apply if passenger is not available after the 15-minute wait period.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#F9A825]/10 border-l-4 border-[#F9A825] p-4 rounded">
                <h3 className="font-semibold text-[#004C97] mb-2">Advance Booking</h3>
                <p className="text-gray-700">
                  We recommend booking <strong>24 hours in advance</strong> for wheelchair transportation and at least <strong>4 hours in advance</strong> for ambulatory transport (subject to availability).
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4 flex items-center">
              <Shield className="w-6 h-6 mr-2 text-[#F9A825]" />
              3. Passenger Conduct & Safety
            </h2>
            <p className="text-gray-700 mb-4">
              To ensure safety for all passengers and staff:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Passengers must follow driver instructions regarding seatbelts and wheelchair securement.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Abusive, threatening, or violent behavior will not be tolerated and may result in immediate termination of service.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Smoking, vaping, or consumption of alcohol/drugs in vehicles is strictly prohibited.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">We reserve the right to refuse service for safety violations or non-compliance.</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">4. Liability & Limitations</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Service Delays:</strong> MoveraCare is not responsible for delays caused by traffic, weather conditions, or facility intake delays beyond our reasonable control.
              </p>
              <p>
                <strong>Medical Care:</strong> Our drivers are trained in passenger assistance and CPR, but MoveraCare does not provide medical care or medical advice. We are a transportation service only.
              </p>
              <p>
                <strong>Personal Belongings:</strong> Passengers are responsible for their personal belongings. MoveraCare is not liable for lost, stolen, or damaged items.
              </p>
              <p>
                <strong>Insurance:</strong> All vehicles are fully insured with commercial liability coverage. Certificate of Insurance available upon request.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">5. Payment Terms</h2>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Payment is due at time of service unless pre-arranged billing is in place.</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">We accept cash, credit cards, and direct insurance billing (where applicable).</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Outstanding balances over 30 days may be subject to collection action.</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">6. Modifications to Terms</h2>
            <p className="text-gray-700">
              MoveraCare reserves the right to modify these Terms of Service at any time. Updated terms will be posted on our website with a new effective date. Continued use of our services constitutes acceptance of modified terms.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">7. Contact Information</h2>
            <div className="bg-gray-50 p-6 rounded-lg border-2 border-[#F9A825]">
              <p className="font-semibold text-[#004C97] mb-2">MoveraCare Services LLC (DBA MoveraCare Mobility)</p>
              <p className="text-gray-700">Richmond, Texas</p>
              <p className="text-gray-700 mt-3">Toll-Free: (832) 925-2128</p>
              <p className="text-gray-700">Local: (832) 925-2128</p>
              <p className="text-gray-700">Fax: (713) 583-2334</p>
              <p className="text-gray-700">Email: dispatch@moveracaremobility.com</p>
            </div>
          </div>

          <div className="border-t pt-6">
            <p className="text-sm text-gray-600">
              By using MoveraCare's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-[#004C97] text-white hover:bg-[#003D7A]">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;